// src/index.js
import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import App from './App';
import {
  PublicLayout,
  StaffLayout,
  AlumLayout,
  TokenOperationalLayout,
  KioskOperationalLayout,
  StaffKioskLayout,
} from './Layout';
import ThankYouPage from './pages/turkeydrop/thankyou';
import AuthProvider from './Components/AuthProvider';
import RootRoute from './Components/RootRoute';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivateRoute from './Components/PrivateRoute';
import UnauthorizedPage from './pages/UnauthorizedPage';

// 👇 NEW: RSVP page route (public)
import TurkeyDropRSVP from './pages/turkeydrop';
import TurkeyDrop2025 from './pages/turkeydrop/turkeydrop2025';
import WhiteChristmas from './pages/whitechristmas/index.jsx';
import WhiteChristmasThankYou from './pages/whitechristmas/thankyou.jsx';
import MailingListSignup from './pages/signup';
import SignupThankYou from './pages/signup/thankyou';
import OpenHouseThankYou from './pages/openhouse/thankyou';
import OpenHouseRsvpPage from './pages/openhouse';
import OpenHouseFeedbackPage from './pages/openhouse/feedback';
import OpenHouseWalkInPage from './pages/openhouse/walk-in';
import MstWebinarSeries2026Page from './pages/mst-webinar-series-2026';
import MstWebinarSeries2026ThankYou from './pages/mst-webinar-series-2026/thankyou';
import DynamicEventPage from './pages/events/DynamicEventPage';
import PintsForPatriotsPage from './pages/pints-for-patriots';
import RockinRoadHomePage from './pages/rockin-road-home';
import RsvpManagePage from './pages/rockin-road-home/RsvpManagePage';
import TicketCancelPage from './pages/rockin-road-home/TicketCancelPage';
import TicketCheckinPage from './pages/rockin-road-home/TicketCheckinPage';
import HorseThief from './pages/pints/HorseThief';
import OpenOutcry from './pages/pints/OpenOutcry';
import PintsThankYou from './pages/pints/ThankYou';
import ChowCallThankYou from './pages/chow-call/ThankYou';
import ChowCallVfw981 from './pages/chow-call/Vfw981';
import ChowCall from './pages/sd/ChowCall';
import SaluteSocialJimmys from './pages/sd/SaluteSocialJimmys';
import SaluteSocialMcps from './pages/sd/SaluteSocialMcps';
import SaluteSocialCaliforniaWildAles from './pages/sd/SaluteSocialCaliforniaWildAles';
import SanDiegoSaluteAndSocial from './pages/sd/SanDiegoSaluteAndSocial';
import SdThankYou from './pages/sd/ThankYou';
import HendosEventPage from './pages/hendos/HendosEventPage';
import PrivacyPolicyPage from './pages/privacy-policy';
import NoIndex from './Components/NoIndex';
import { INTERNAL_ROUTE_ROLES } from './auth/authorization';

const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const MeDashboardPage = lazy(() => import('./pages/MeDashboardPage'));
const AlumDashboardPage = lazy(() => import('./pages/alum/AlumDashboardPage'));
const AlumAarNewPage = lazy(() => import('./pages/alum/AlumAarNewPage'));
const AlumQrNewPage = lazy(() => import('./pages/alum/AlumQrNewPage'));
const AlumSupportRequestNewPage = lazy(() => import('./pages/alum/AlumSupportRequestNewPage'));
const AlumCommandCenterPage = lazy(() => import('./pages/admin/alum/AlumCommandCenterPage'));
const AlumCommandCenterDetailPage = lazy(() => import('./pages/admin/alum/AlumCommandCenterDetailPage'));
const AlumRosterNewPage = lazy(() => import('./pages/admin/alum/AlumRosterNewPage'));
const AlumRosterEditPage = lazy(() => import('./pages/admin/alum/AlumRosterEditPage'));
const VolunteerNetworkPage = lazy(() => import('./pages/admin/volunteers/VolunteerNetworkPage'));
const VolunteerFormPage = lazy(() => import('./pages/admin/volunteers/VolunteerFormPage'));
const VolunteerDetailPage = lazy(() => import('./pages/admin/volunteers/VolunteerDetailPage'));
const AarHomePage = lazy(() => import('./pages/aars/AarHomePage'));
const AarHistoricalReviewQueuePage = lazy(() => import('./pages/aars/AarHistoricalReviewQueuePage'));
const AarHistoricalReviewDetailPage = lazy(() => import('./pages/aars/AarHistoricalReviewDetailPage'));
const AarNewPage = lazy(() => import('./pages/aars/AarNewPage'));
const AarEventShellPage = lazy(() => import('./pages/aars/AarEventShellPage'));
const AarDetailPage = lazy(() => import('./pages/aars/AarDetailPage'));
const AarEditPage = lazy(() => import('./pages/aars/AarEditPage'));
const AarPhotosPage = lazy(() => import('./pages/aars/AarPhotosPage'));
const AarPhotoLibraryPage = lazy(() => import('./pages/aars/AarPhotoLibraryPage'));
const AarReportingPage = lazy(() => import('./pages/admin-aars-reporting/AarReportingPage'));
const GetCareAnalyticsPage = lazy(() => import('./pages/admin-get-care/GetCareAnalyticsPage'));
const VocScoreboardPage = lazy(() => import('./pages/admin-voc-scoreboard/VocScoreboardPage'));
const VocMonthlyImportPage = lazy(() => import('./pages/admin-voc-monthly-import/VocMonthlyImportPage'));
const RoadHomeLiveImportPage = lazy(() => import('./pages/admin-road-home-live-import/RoadHomeLiveImportPage'));
const DataStewardshipPage = lazy(() => import('./pages/admin-data-stewardship/DataStewardshipPage'));
const MissionControlPage = lazy(() => import('./pages/admin-mission-control/MissionControlPage'));
const ReleaseStatusPage = lazy(() => import('./pages/admin-release-status/ReleaseStatusPage'));
const AdminVatPulseNewPage = lazy(() => import('./pages/vat-pulse/AdminVatPulseNewPage'));
const VatPulsePage = lazy(() => import('./pages/vat-pulse/VatPulsePage'));
const TurkeyDashboard = lazy(() => import('./pages/turkeydrop/turkeydrop2025/TurkeyDashboard'));
const CheckinMode = lazy(() => import('./pages/turkeydrop/turkeydrop2025/CheckinMode'));
const AdminEventsPage = lazy(() => import('./pages/admin-events/AdminEventsPage'));
const EventPageAnalyticsPage = lazy(() => import('./pages/admin-events/EventPageAnalyticsPage'));
const EventPageBuilder = lazy(() => import('./pages/admin-events/EventPageBuilder'));
const PublicEventOperationsPage = lazy(() => import('./pages/admin-events/PublicEventOperationsPage'));
const MediaLibraryPage = lazy(() => import('./pages/admin-media/MediaLibraryPage'));
const EventCheckinIndexPage = lazy(() => import('./pages/event-checkin/EventCheckinIndexPage'));
const EventCheckinStationPage = lazy(() => import('./pages/event-checkin/EventCheckinStationPage'));
const PhotoBoothPage = lazy(() => import('./pages/photo-booth/PhotoBoothPage'));

const withRouteSuspense = (element) => (
  <Suspense fallback={null}>
    {element}
  </Suspense>
);

const RockinRoadHomeRedirect = () => {
  const location = useLocation();

  return (
    <Navigate
      to={{ pathname: '/road-home-live', search: location.search }}
      replace
    />
  );
};

const HendosRedirect = () => {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: '/events/salute-social-hendos-2026',
        search: location.search,
      }}
      replace
    />
  );
};

const Root = () => (
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

          {/* STAFF AUTHENTICATED */}
          <Route element={<StaffLayout />}>
            <Route
              path="generator"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.generator}>
                    <App />
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="history"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.generator}>
                    {withRouteSuspense(<HistoryPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="analytics"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminOnly}>
                    {withRouteSuspense(<AnalyticsPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<MeDashboardPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarHomePage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars/new"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarNewPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars/photos"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarPhotoLibraryPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars/historical-review"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarHistoricalReviewQueuePage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars/historical-review/:rowId"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarHistoricalReviewDetailPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars/event/:eventId"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarEventShellPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars/:aarId"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarDetailPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars/:aarId/edit"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarEditPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/aars/:aarId/photos"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarPhotosPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="dashboard"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                    <Navigate to="/insights" replace />
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="insights"
              element={
                <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                  {withRouteSuspense(<InsightsPage />)}
                </PrivateRoute>
              }
            />

            {/* Retired Command Center. Compatibility redirect only; not in navigation. */}
            <Route
              path="admin/dashboard"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                    <Navigate to="/me" replace />
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/alum"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                    {withRouteSuspense(<AlumCommandCenterPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/alum/new"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                    {withRouteSuspense(<AlumRosterNewPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/alum/:alumId"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                    {withRouteSuspense(<AlumCommandCenterDetailPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/alum/:alumId/edit"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                    {withRouteSuspense(<AlumRosterEditPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/volunteers"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<VolunteerNetworkPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/volunteers/new"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<VolunteerFormPage mode="create" />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/volunteers/:volunteerId"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<VolunteerDetailPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/volunteers/:volunteerId/edit"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<VolunteerFormPage mode="edit" />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/aars-reporting"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AarReportingPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/get-care"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                    {withRouteSuspense(<GetCareAnalyticsPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/voc-scoreboard"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.reporting}>
                    {withRouteSuspense(<VocScoreboardPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/voc-monthly-import"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminOnly}>
                    {withRouteSuspense(<VocMonthlyImportPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/road-home-live-import"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminOnly}>
                    {withRouteSuspense(<RoadHomeLiveImportPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/vat-pulse/new"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.staff}>
                    {withRouteSuspense(<AdminVatPulseNewPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="admin/rsvps"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminRsvps}>
                    {withRouteSuspense(<TurkeyDashboard />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/checkin"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminCheckin}>
                    {withRouteSuspense(<CheckinMode />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/data-stewardship"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminOnly}>
                    {withRouteSuspense(<DataStewardshipPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/mission-control"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminOnly}>
                    {withRouteSuspense(<MissionControlPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/release-status"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminOnly}>
                    {withRouteSuspense(<ReleaseStatusPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/events"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminContent}>
                    {withRouteSuspense(<AdminEventsPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/events/new"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminContent}>
                    {withRouteSuspense(<EventPageBuilder />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/events/:id/edit"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminContent}>
                    {withRouteSuspense(<EventPageBuilder />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/events/:id/analytics"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminContent}>
                    {withRouteSuspense(<EventPageAnalyticsPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/events/:eventId/operations"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminContent}>
                    {withRouteSuspense(<PublicEventOperationsPage />)}
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="admin/media"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.adminContent}>
                    {withRouteSuspense(<MediaLibraryPage />)}
                  </PrivateRoute>
                </>
              }
            />
          </Route>

          {/* ALUM AUTHENTICATED */}
          <Route element={<AlumLayout />}>
            <Route
              path="me/alum"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.alum}>
                    {withRouteSuspense(<AlumDashboardPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/alum/aars/new"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.alum}>
                    {withRouteSuspense(<AlumAarNewPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/alum/qr/new"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.alum}>
                    {withRouteSuspense(<AlumQrNewPage />)}
                  </PrivateRoute>
                </>
              }
            />

            <Route
              path="me/alum/support/new"
              element={
                <>
                  <NoIndex />
                  <PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.alum}>
                    {withRouteSuspense(<AlumSupportRequestNewPage />)}
                  </PrivateRoute>
                </>
              }
            />
          </Route>

          <Route element={<StaffKioskLayout />}>
            <Route path="/check-in" element={
              <><NoIndex /><PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.eventCheckin}>
                {withRouteSuspense(<EventCheckinIndexPage />)}
              </PrivateRoute></>
            } />
            <Route path="/check-in/:eventId" element={
              <><NoIndex /><PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.eventCheckin}>
                {withRouteSuspense(<EventCheckinStationPage />)}
              </PrivateRoute></>
            } />
            <Route path="/photo-booth/:eventId" element={
              <><NoIndex /><PrivateRoute allowedRoles={INTERNAL_ROUTE_ROLES.photoBooth}>
                {withRouteSuspense(<PhotoBoothPage />)}
              </PrivateRoute></>
            } />
          </Route>

          {/* PUBLIC */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<RootRoute />} />
            <Route path="/turkeydrop" element={<TurkeyDropRSVP />} />
            <Route path="/turkeydrop2025/thankyou" element={<ThankYouPage />} />
            <Route path="/turkeydrop2025" element={<TurkeyDrop2025 />} />
            <Route path="/whitechristmas" element={<WhiteChristmas />} />
            <Route
              path="/whitechristmas/thankyou"
              element={<WhiteChristmasThankYou />}
            />
            <Route path="/signup" element={<MailingListSignup />} />
            <Route path="/signup/thankyou" element={<SignupThankYou />} />
            <Route path="/events/road-home-live" element={<RockinRoadHomeRedirect />} />
            <Route path="/events/rockin-road-home" element={<RockinRoadHomeRedirect />} />
            <Route path="/events/rockin-with-road-home" element={<RockinRoadHomeRedirect />} />
            <Route
              path="/events/salute-social-hendos-2026"
              element={<HendosEventPage />}
            />
            <Route path="/events/:slug" element={<DynamicEventPage />} />
            <Route path="/open-house" element={<OpenHouseRsvpPage />} />
            <Route path="/open-house/feedback" element={<OpenHouseFeedbackPage />} />
            <Route path="/open-house/thankyou" element={<OpenHouseThankYou />} />
            <Route path="/vat-pulse" element={withRouteSuspense(<VatPulsePage />)} />
            <Route
              path="/mst-webinar-series-2026"
              element={<MstWebinarSeries2026Page />}
            />
            <Route
              path="/mst-webinar-series-2026/thankyou"
              element={<MstWebinarSeries2026ThankYou />}
            />
            <Route path="/pints-for-patriots" element={<PintsForPatriotsPage />} />
            <Route path="/rockin-road-home" element={<RockinRoadHomeRedirect />} />
            <Route path="/rockin-with-road-home" element={<RockinRoadHomeRedirect />} />
            <Route path="/road-home-live" element={<RockinRoadHomePage />} />
            <Route path="/hendos" element={<HendosRedirect />} />
            <Route path="/chow-call-vfw981" element={<ChowCallVfw981 />} />
            <Route path="/chow-call" element={<ChowCallVfw981 />} />
            <Route path="/chow-call/thank-you" element={<ChowCallThankYou />} />
            <Route path="/pints/horsethief" element={<HorseThief />} />
            <Route path="/pints/openoutcry" element={<OpenOutcry />} />
            <Route path="/pints/thank-you" element={<PintsThankYou />} />
            <Route path="/sd" element={<SanDiegoSaluteAndSocial />} />
            <Route path="/sd/chow-call" element={<ChowCall />} />
            <Route path="/sd/chow-call-novo" element={<ChowCall />} />
            <Route
              path="/sandiego-salute-and-social"
              element={<SanDiegoSaluteAndSocial />}
            />
            <Route
              path="/sd/salute-social-jimmys"
              element={<SaluteSocialJimmys />}
            />
            <Route path="/sd/salute-social-mcps" element={<SaluteSocialMcps />} />
            <Route
              path="/sd/salute-social-california-wild-ales"
              element={<SaluteSocialCaliforniaWildAles />}
            />
            <Route path="/sd/thank-you" element={<SdThankYou />} />
            <Route
              path="/unauthorized"
              element={
                <>
                  <NoIndex />
                  <UnauthorizedPage />
                </>
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/forgot-password"
              element={
                <>
                  <NoIndex />
                  <ForgotPasswordPage />
                </>
              }
            />
            <Route
              path="/reset-password"
              element={
                <>
                  <NoIndex />
                  <ResetPasswordPage />
                </>
              }
            />
          </Route>

          {/* TOKEN OPERATIONAL */}
          <Route element={<TokenOperationalLayout />}>
            <Route
              path="/rsvp/manage/:token"
              element={
                <>
                  <NoIndex />
                  <RsvpManagePage />
                </>
              }
            />
            <Route
              path="/tickets/cancel/:token"
              element={
                <>
                  <NoIndex />
                  <TicketCancelPage />
                </>
              }
            />
            <Route
              path="/tickets/checkin/:token"
              element={
                <>
                  <NoIndex />
                  <TicketCheckinPage />
                </>
              }
            />
          </Route>

          {/* KIOSK OPERATIONAL */}
          <Route element={<KioskOperationalLayout />}>
            <Route path="/open-house/walk-in" element={<OpenHouseWalkInPage />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Root />);
